import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, Check, Leaf, Fish, Flame, Apple, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";

interface DietaryPreferencesProps {
  onComplete: (preferences: DietaryPreferencesData) => void;
}

export interface DietaryPreferencesData {
  lifestyles: string[];
  allergens: string[];
}

const lifestyleOptions = [
  { id: "vegan", label: "Vegan", icon: Leaf },
  { id: "vegetarian", label: "Vegetarian", icon: Leaf },
  { id: "pescetarian", label: "Pescetarian", icon: Fish },
  { id: "keto", label: "Keto", icon: Flame },
  { id: "paleo", label: "Paleo", icon: Apple },
];

const allergenOptions = [
  { id: "gluten-free", label: "Gluten-Free" },
  { id: "dairy-free", label: "Dairy-Free" },
  { id: "nut-free", label: "Nut-Free" },
  { id: "egg-free", label: "Egg-Free" },
  { id: "soy-free", label: "Soy-Free" },
  { id: "wheat-free", label: "Wheat-Free" },
  { id: "fish-free", label: "Fish-Free" },
  { id: "shellfish-free", label: "Shellfish-Free" },
  { id: "sesame-free", label: "Sesame-Free" },
];

export const DietaryPreferences = ({ onComplete }: DietaryPreferencesProps) => {
  const [selectedLifestyles, setSelectedLifestyles] = useState<string[]>([]);
  const [selectedAllergens, setSelectedAllergens] = useState<string[]>([]);

  const toggleLifestyle = (id: string) => {
    setSelectedLifestyles((prev) => {
      if (prev.includes(id)) {
        return prev.filter((l) => l !== id);
      }
      // Only allow one lifestyle selection
      if (prev.length >= 1) {
        toast({
          title: "Limit reached",
          description: "You can only select one lifestyle. Deselect the current one first.",
          duration: 3000,
        });
        return prev;
      }
      return [...prev, id];
    });
  };

  const toggleAllergen = (id: string) => {
    setSelectedAllergens((prev) =>
      prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id]
    );
  };

  const handleContinue = () => {
    onComplete({
      lifestyles: selectedLifestyles,
      allergens: selectedAllergens,
    });
  };

  const totalSelected = selectedLifestyles.length + selectedAllergens.length;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-background z-50 flex flex-col"
    >
      <div className="flex-1 px-6 py-8 overflow-y-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <h1 className="text-2xl font-semibold text-foreground mb-2">
            Dietary Preferences
          </h1>
          <p className="text-muted-foreground">
            Tell us about your diet so we only suggest safe foods.
          </p>
        </motion.div>

        {/* Section 1: Lifestyles */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-6"
        >
          <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3">
            Lifestyles
          </h2>
          <div className="grid grid-cols-2 gap-3">
            {lifestyleOptions.map((option, index) => {
              const isSelected = selectedLifestyles.includes(option.id);
              const Icon = option.icon;
              return (
                <motion.button
                  key={option.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.03 }}
                  onClick={() => toggleLifestyle(option.id)}
                  className={`relative p-4 rounded-2xl border-2 text-left transition-all ${
                    isSelected
                      ? "border-primary bg-secondary"
                      : "border-border bg-card hover:border-muted-foreground/30"
                  }`}
                >
                  {isSelected && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="absolute top-3 right-3 w-5 h-5 rounded-full gradient-primary flex items-center justify-center"
                    >
                      <Check className="w-3 h-3 text-primary-foreground" />
                    </motion.div>
                  )}
                  <div
                    className={`w-10 h-10 rounded-xl mb-3 flex items-center justify-center ${
                      isSelected ? "gradient-primary" : "bg-secondary"
                    }`}
                  >
                    <Icon
                      className={`w-5 h-5 ${
                        isSelected ? "text-primary-foreground" : "text-metabolic-500"
                      }`}
                    />
                  </div>
                  <h3 className="font-medium text-foreground text-sm">
                    {option.label}
                  </h3>
                </motion.button>
              );
            })}
          </div>
        </motion.div>

        {/* Section 2: Allergens & Sensitivities */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-3 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" />
            Allergens & Sensitivities
          </h2>
          <div className="grid grid-cols-3 gap-2">
            {allergenOptions.map((option, index) => {
              const isSelected = selectedAllergens.includes(option.id);
              return (
                <motion.button
                  key={option.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 + index * 0.02 }}
                  onClick={() => toggleAllergen(option.id)}
                  className={`relative p-3 rounded-xl border-2 text-center transition-all ${
                    isSelected
                      ? "border-primary bg-secondary"
                      : "border-border bg-card hover:border-muted-foreground/30"
                  }`}
                >
                  {isSelected && (
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      className="absolute top-1.5 right-1.5 w-4 h-4 rounded-full gradient-primary flex items-center justify-center"
                    >
                      <Check className="w-2.5 h-2.5 text-primary-foreground" />
                    </motion.div>
                  )}
                  <span className="text-xs font-medium text-foreground">
                    {option.label}
                  </span>
                </motion.button>
              );
            })}
          </div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="p-6 pb-10 border-t border-border bg-background"
      >
        <Button
          onClick={handleContinue}
          className="w-full h-14 text-base font-medium gradient-primary hover:opacity-90 transition-opacity"
        >
          {totalSelected > 0
            ? `Next: Health Goals (${totalSelected} selected)`
            : "Next: Health Goals"}
          <ArrowRight className="w-5 h-5 ml-2" />
        </Button>
        <p className="text-xs text-muted-foreground text-center mt-3">
          You can skip this if you have no restrictions
        </p>
      </motion.div>
    </motion.div>
  );
};
